import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser, userSelector } from './userSlice';
import { useNavigate } from 'react-router-dom';

import logo from "../../assets/logo/LOGORKS2.png";
import { alertService } from '../../services/AlertService';
import { Colors } from "../../assets/Colors";
import "./Login.css";

// ── Animasi Scene Pelabuhan ────────────────────────────────
const HarborScene = () => (
  <svg
    viewBox="0 0 800 600"
    xmlns="http://www.w3.org/2000/svg"
    className="w-full h-full"
    style={{ minHeight: "100%" }}
  >
    <defs>
      {/* Gradien langit */}
      <linearGradient id="skyGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#0f2027" />
        <stop offset="50%" stopColor="#203a43" />
        <stop offset="100%" stopColor="#2c5364" />
      </linearGradient>
      {/* Gradien air */}
      <linearGradient id="seaGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1a4a6e" />
        <stop offset="100%" stopColor="#0d2b40" />
      </linearGradient>
      {/* Gradien kapal */}
      <linearGradient id="shipGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#e8e8e8" />
        <stop offset="100%" stopColor="#b0b0b0" />
      </linearGradient>
      {/* Cahaya bulan */}
      <radialGradient id="moonGlow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#fffde7" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#fffde7" stopOpacity="0" />
      </radialGradient>
      {/* Refleksi air */}
      <linearGradient id="reflectionGrad" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#f18543" stopOpacity="0.3" />
        <stop offset="100%" stopColor="#f18543" stopOpacity="0" />
      </linearGradient>

      <style>{`
        @keyframes wave1 {
          0%   { d: path("M0,380 C100,365 200,395 300,380 C400,365 500,395 600,380 C700,365 800,395 800,380 L800,600 L0,600 Z"); }
          50%  { d: path("M0,385 C100,400 200,370 300,385 C400,400 500,370 600,385 C700,400 800,370 800,385 L800,600 L0,600 Z"); }
          100% { d: path("M0,380 C100,365 200,395 300,380 C400,365 500,395 600,380 C700,365 800,395 800,380 L800,600 L0,600 Z"); }
        }
        @keyframes wave2 {
          0%   { d: path("M0,395 C150,380 250,410 400,395 C550,380 650,410 800,395 L800,600 L0,600 Z"); }
          50%  { d: path("M0,405 C150,420 250,390 400,405 C550,420 650,390 800,405 L800,600 L0,600 Z"); }
          100% { d: path("M0,395 C150,380 250,410 400,395 C550,380 650,410 800,395 L800,600 L0,600 Z"); }
        }
        @keyframes shipFloat {
          0%, 100% { transform: translateY(0px) rotate(-0.5deg); }
          50%       { transform: translateY(-8px) rotate(0.5deg); }
        }
        @keyframes craneSwing {
          0%, 100% { transform: rotate(0deg); }
          50%       { transform: rotate(3deg); }
        }
        @keyframes containerDrop {
          0%, 80%, 100% { transform: translateY(0); }
          40%            { transform: translateY(6px); }
        }
        @keyframes starTwinkle {
          0%, 100% { opacity: 0.2; }
          50%       { opacity: 1; }
        }
        @keyframes smokePuff {
          0%   { transform: translateY(0) scaleX(1); opacity: 0.6; }
          100% { transform: translateY(-40px) scaleX(2); opacity: 0; }
        }
        @keyframes moonPulse {
          0%, 100% { opacity: 0.9; }
          50%       { opacity: 1; }
        }
        @keyframes reflectionShimmer {
          0%, 100% { opacity: 0.4; }
          50%       { opacity: 0.7; }
        }
        @keyframes buoyBob {
          0%, 100% { transform: translateY(0) rotate(-5deg); }
          50%       { transform: translateY(-5px) rotate(5deg); }
        }
        @keyframes lightBlink {
          0%, 90%, 100% { opacity: 1; }
          45%, 55%       { opacity: 0.1; }
        }

        .wave-1 { animation: wave1 4s ease-in-out infinite; }
        .wave-2 { animation: wave2 5s ease-in-out infinite; }
        .ship   { animation: shipFloat 4s ease-in-out infinite; transform-origin: center 320px; }
        .crane-arm { animation: craneSwing 6s ease-in-out infinite; transform-origin: 580px 200px; }
        .container-hang { animation: containerDrop 6s ease-in-out infinite; }
        .star   { animation: starTwinkle 3s ease-in-out infinite; }
        .smoke  { animation: smokePuff 3s ease-out infinite; }
        .moon   { animation: moonPulse 4s ease-in-out infinite; }
        .reflection { animation: reflectionShimmer 3s ease-in-out infinite; }
        .buoy   { animation: buoyBob 2.5s ease-in-out infinite; }
        .light  { animation: lightBlink 2s ease-in-out infinite; }
      `}</style>
    </defs>

    {/* ── Langit ── */}
    <rect width="800" height="600" fill="url(#skyGrad)" />

    {/* Bintang-bintang */}
    {[
      [60,30],[120,55],[200,20],[280,45],[360,15],[440,40],[520,25],[620,50],
      [700,30],[750,60],[80,80],[180,90],[340,75],[500,85],[660,70],[740,95],
    ].map(([cx, cy], i) => (
      <circle
        key={i}
        cx={cx} cy={cy} r="1.5"
        fill="white"
        className="star"
        style={{ animationDelay: `${i * 0.3}s` }}
      />
    ))}

    {/* Bulan + glow */}
    <circle cx="680" cy="80" r="55" fill="url(#moonGlow)" className="moon" />
    <circle cx="680" cy="80" r="32" fill="#fffde7" opacity="0.95" className="moon" />
    <circle cx="692" cy="72" r="22" fill="#f5e6c8" opacity="0.5" />

    {/* ── Dermaga / Background kota ── */}
    {/* Gedung-gedung kota samar */}
    <rect x="0"   y="260" width="60"  height="120" fill="#1a3045" opacity="0.6" />
    <rect x="20"  y="240" width="30"  height="140" fill="#1a3045" opacity="0.5" />
    <rect x="65"  y="280" width="50"  height="100" fill="#1b3550" opacity="0.5" />
    <rect x="130" y="255" width="40"  height="125" fill="#1a3045" opacity="0.5" />
    <rect x="140" y="235" width="20"  height="145" fill="#1c3652" opacity="0.4" />
    {/* Lampu gedung */}
    {[[25,255],[35,265],[80,285],[95,270],[135,260],[145,245]].map(([x,y],i)=>(
      <rect key={i} x={x} y={y} width="4" height="4" fill="#f18543" opacity="0.7"
        style={{ animationDelay:`${i*0.5}s` }} />
    ))}

    {/* ── Crane Pelabuhan ── */}
    {/* Tiang utama */}
    <rect x="570" y="195" width="14" height="185" fill="#c0392b" />
    {/* Kaki crane */}
    <rect x="555" y="365" width="44" height="15" fill="#c0392b" />
    <polygon points="555,365 540,380 599,380" fill="#922b21" />
    {/* Lengan crane */}
    <g className="crane-arm">
      <rect x="540" y="185" width="160" height="12" fill="#e74c3c" rx="3" />
      <rect x="688" y="120" width="12" height="77" fill="#e74c3c" />
      {/* Kabel crane */}
      <line x1="660" y1="197" x2="655" y2="255" stroke="#aaa" strokeWidth="1.5" />
      {/* Kontainer yang digantung */}
      <g className="container-hang">
        <rect x="638" y="255" width="34" height="22" fill="#27ae60" rx="2" />
        <line x1="645" y1="255" x2="645" y2="277" stroke="#1e8449" strokeWidth="1" />
        <line x1="665" y1="255" x2="665" y2="277" stroke="#1e8449" strokeWidth="1" />
      </g>
      {/* Counter weight */}
      <rect x="540" y="187" width="30" height="20" fill="#922b21" rx="2" />
    </g>
    {/* Lampu crane */}
    <circle cx="700" cy="122" r="5" fill="#f1c40f" className="light" />

    {/* ── Dermaga ── */}
    <rect x="0" y="370" width="820" height="18" fill="#2c3e50" />
    {/* Tiang dermaga */}
    {[80,160,240,320,400,480,560].map((x,i) => (
      <rect key={i} x={x} y="370" width="8" height="30" fill="#1a252f" />
    ))}
    {/* Bollard / tiang tambat */}
    {[100,200,300,400,500].map((x,i)=>(
      <g key={i}>
        <rect x={x-4} y="360" width="8" height="14" fill="#7f8c8d" rx="2"/>
        <ellipse cx={x} cy="359" rx="6" ry="3" fill="#95a5a6"/>
      </g>
    ))}

    {/* Kontainer di dermaga */}
    {[
      { x:30,  y:330, w:55, h:38, c:"#e74c3c" },
      { x:90,  y:330, w:55, h:38, c:"#3498db" },
      { x:150, y:330, w:55, h:38, c:"#f39c12" },
      { x:210, y:330, w:55, h:38, c:"#27ae60" },
      { x:30,  y:295, w:55, h:38, c:"#3498db" },
      { x:90,  y:295, w:55, h:38, c:"#e74c3c" },
      { x:150, y:295, w:55, h:38, c:"#8e44ad" },
      { x:270, y:330, w:55, h:38, c:"#e74c3c" },
      { x:330, y:330, w:55, h:38, c:"#27ae60" },
      { x:270, y:295, w:55, h:38, c:"#f39c12" },
    ].map((r,i)=>(
      <g key={i}>
        <rect x={r.x} y={r.y} width={r.w} height={r.h} fill={r.c} rx="2"/>
        <line x1={r.x+18} y1={r.y} x2={r.x+18} y2={r.y+r.h} stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
        <line x1={r.x+36} y1={r.y} x2={r.x+36} y2={r.y+r.h} stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
        <rect x={r.x} y={r.y} width={r.w} height="4" fill="rgba(255,255,255,0.15)" rx="1"/>
      </g>
    ))}

    {/* ── Kapal Kargo ── */}
    <g className="ship">
      {/* Lambung kapal */}
      <path d="M390,295 L680,295 L700,340 L370,340 Z" fill="#2c3e50" />
      <path d="M370,340 L700,340 L690,360 L380,360 Z" fill="#34495e" />
      {/* Geladak */}
      <rect x="395" y="265" width="280" height="32" fill="#bdc3c7" rx="3" />
      {/* Anjungan / superstruktur */}
      <rect x="620" y="215" width="80" height="52" fill="#ecf0f1" rx="4" />
      <rect x="630" y="200" width="62" height="18" fill="#dfe6e9" rx="3" />
      {/* Jendela anjungan */}
      {[[630,220],[652,220],[674,220],[630,235],[652,235],[674,235]].map(([x,y],i)=>(
        <rect key={i} x={x} y={y} width="14" height="10" fill="#74b9ff" rx="1" opacity="0.9"/>
      ))}
      {/* Tiang sinyal */}
      <rect x="657" y="170" width="4" height="32" fill="#7f8c8d" />
      <line x1="659" y1="170" x2="640" y2="178" stroke="#7f8c8d" strokeWidth="1.5"/>
      <line x1="659" y1="170" x2="678" y2="178" stroke="#7f8c8d" strokeWidth="1.5"/>
      {/* Lampu navigasi */}
      <circle cx="659" cy="168" r="4" fill="#e74c3c" className="light"
        style={{ animationDelay:"1s" }} />
      {/* Kontainer di atas kapal */}
      {[
        {x:410,y:235,c:"#e74c3c"},{x:458,y:235,c:"#3498db"},{x:506,y:235,c:"#f39c12"},
        {x:554,y:235,c:"#27ae60"},{x:602,y:235,c:"#e74c3c"},
        {x:410,y:205,c:"#8e44ad"},{x:458,y:205,c:"#e74c3c"},{x:506,y:205,c:"#3498db"},
        {x:554,y:205,c:"#f39c12"},
      ].map((r,i)=>(
        <g key={i}>
          <rect x={r.x} y={r.y} width="44" height="28" fill={r.c} rx="1"/>
          <line x1={r.x+15} y1={r.y} x2={r.x+15} y2={r.y+28} stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
          <line x1={r.x+29} y1={r.y} x2={r.x+29} y2={r.y+28} stroke="rgba(0,0,0,0.2)" strokeWidth="1"/>
          <rect x={r.x} y={r.y} width="44" height="4" fill="rgba(255,255,255,0.15)"/>
        </g>
      ))}
      {/* Asap cerobong */}
      <rect x="638" y="190" width="8" height="15" fill="#7f8c8d" rx="2"/>
      <ellipse cx="642" cy="188" rx="6" ry="4" fill="#95a5a6" className="smoke" />
      <ellipse cx="642" cy="178" rx="8" ry="5" fill="#7f8c8d" className="smoke"
        style={{ animationDelay: "1s" }} />
      {/* Lambung merah di bawah waterline */}
      <path d="M372,354 L688,354 L682,368 L378,368 Z" fill="#c0392b" />
    </g>

    {/* ── Pelampung / Buoy ── */}
    <g className="buoy" style={{ animationDelay: "0.5s" }}>
      <ellipse cx="355" cy="375" rx="8" ry="12" fill="#e74c3c" />
      <ellipse cx="355" cy="368" rx="8" ry="5" fill="#f39c12" />
      <line x1="355" y1="363" x2="355" y2="358" stroke="#7f8c8d" strokeWidth="2"/>
      <circle cx="355" cy="357" r="3" fill="#f1c40f" className="light" />
    </g>

    {/* ── Refleksi cahaya di air ── */}
    <ellipse cx="680" cy="410" rx="40" ry="8" fill="#fffde7" opacity="0.12" className="reflection"/>
    <rect x="650" y="390" width="5" height="60" fill="#fffde7" opacity="0.08"
      transform="skewX(5)" className="reflection"/>
    {/* Refleksi lampu crane */}
    <rect x="697" y="388" width="3" height="50" fill="#f1c40f" opacity="0.1"
      className="reflection" style={{ animationDelay:"0.5s" }}/>
    {/* Refleksi warna-warni kontainer */}
    <rect x="450" y="378" width="3" height="40" fill="#3498db" opacity="0.12"
      transform="skewX(3)" className="reflection" style={{ animationDelay:"1s" }}/>
    <rect x="500" y="382" width="3" height="35" fill="#f39c12" opacity="0.1"
      transform="skewX(-2)" className="reflection" style={{ animationDelay:"1.5s" }}/>

    {/* ── Ombak laut ── */}
    <path
      className="wave-1"
      d="M0,380 C100,365 200,395 300,380 C400,365 500,395 600,380 C700,365 800,395 800,380 L800,600 L0,600 Z"
      fill="url(#seaGrad)"
      opacity="0.9"
    />
    <path
      className="wave-2"
      d="M0,395 C150,380 250,410 400,395 C550,380 650,410 800,395 L800,600 L0,600 Z"
      fill="#0d2b40"
      opacity="0.7"
    />

    {/* Riak kecil di permukaan */}
    {[[50,390],[200,385],[350,392],[450,387],[600,391],[720,386]].map(([x,y],i)=>(
      <ellipse key={i} cx={x} cy={y} rx="15" ry="2.5"
        fill="rgba(255,255,255,0.08)"
        style={{ animationDelay:`${i*0.7}s` }}
        className="reflection"
      />
    ))}

    {/* ── Overlay gradient bawah untuk blend ── */}
    <rect x="0" y="480" width="800" height="120" fill="#0d2b40" opacity="0.4"/>
  </svg>
);

// ── Login Page ─────────────────────────────────────────────
const LoginPage = () => {
    const dispatch  = useDispatch();
    const navigate  = useNavigate();
    const [autoClose]            = useState(true);
    const [keepAfterRouteChange] = useState(false);
    const [username,     setUsername]     = useState('');
    const [password,     setPassword]     = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [errorStatus,  setErrorStatus]  = useState(false);
    const { handleSubmit } = useForm();
    const { isFetching, isSuccess, isError, errorMessage, message } = useSelector(userSelector);

    const onSubmit = () => dispatch(loginUser({ username, password }));

    useEffect(() => {
        if (isError) setErrorStatus(true);
    }, [isFetching]);

    useEffect(() => {
        if (errorStatus && isError && !isSuccess && !isFetching) {
            setErrorStatus(false);
            alertService.error(errorMessage, { autoClose, keepAfterRouteChange });
        }
        if (isSuccess) {
            alertService.success(message, { autoClose, keepAfterRouteChange });
            setTimeout(() => { navigate('/'); window.location.reload(false); }, 1250);
        }
    });

    useEffect(() => { return () => {}; }, []);

    return (
        <div className="flex h-screen overflow-hidden">

            {/* ── Kiri: Animasi Scene Pelabuhan ── */}
            <div className="hidden md:flex md:w-3/5 relative flex-col items-end justify-end overflow-hidden"
                style={{ background: "#0f2027" }}>

                <HarborScene />

                {/* Overlay branding di atas animasi */}
                <div className="absolute inset-0 pointer-events-none"
                    style={{ background: "linear-gradient(to bottom, rgba(15,32,39,0.6) 0%, rgba(15,32,39,0.1) 50%, rgba(15,32,39,0.75) 100%)" }}>

                    {/* Branding diposisikan di atas — area langit */}
                    <div className="flex flex-col items-center pt-12">
                        <img src={logo} alt="RKS Logo"
                            className="w-44 object-contain mb-3 drop-shadow-2xl" />
                        <h1 className="text-white text-xl font-bold text-center drop-shadow-lg px-8 leading-snug">
                            Shorebase Activities Management System
                        </h1>
                        <p className="text-white text-xs mt-2 opacity-70 text-center px-12">
                            Platform manajemen operasional shorebase terpadu
                        </p>
                    </div>
                </div>
            </div>

            {/* ── Kanan: Form Login ── */}
            <div className="w-full md:w-2/5 flex flex-col items-center justify-center bg-white px-8 md:px-12 relative">

                {/* Logo mobile */}
                <div className="md:hidden mb-8 flex flex-col items-center">
                    <img src={logo} alt="RKS Logo" className="w-40 object-contain mb-2" />
                </div>

                <div className="w-full max-w-sm">
                    <h3 className="text-2xl font-bold text-gray-800 mb-1">Selamat Datang</h3>
                    <p className="text-sm text-gray-500 mb-8">Masukkan kredensial Anda untuk melanjutkan</p>

                    <form onSubmit={handleSubmit(onSubmit)} method="POST">
                        {/* Username */}
                        <div className="mb-5">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="username">
                                Username
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                </span>
                                <input
                                    value={username}
                                    onChange={(e) => setUsername(e.target.value)}
                                    className="w-full pl-10 pr-4 py-3 text-sm border border-gray-200 rounded-xl text-gray-700 focus:outline-none bg-gray-50 transition-all"
                                    id="username" type="text" placeholder="Masukkan username"
                                    autoComplete="username" required
                                />
                            </div>
                        </div>

                        {/* Password */}
                        <div className="mb-7">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5" htmlFor="password">
                                Password
                            </label>
                            <div className="relative">
                                <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                    </svg>
                                </span>
                                <input
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full pl-10 pr-12 py-3 text-sm border border-gray-200 rounded-xl text-gray-700 focus:outline-none bg-gray-50 transition-all"
                                    id="password"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Masukkan password"
                                    autoComplete="current-password" required
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(v => !v)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                                    tabIndex={-1}
                                >
                                    {showPassword ? (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" />
                                        </svg>
                                    ) : (
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                        </svg>
                                    )}
                                </button>
                            </div>
                        </div>

                        {/* Tombol login */}
                        <button
                            type="submit"
                            disabled={isFetching}
                            className="w-full py-3 px-4 text-white text-sm font-semibold rounded-xl shadow-md hover:opacity-90 active:scale-95 transition-all focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                            style={{ background: Colors.primary }}
                        >
                            {isFetching ? (
                                <>
                                    <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"/>
                                    </svg>
                                    Sedang masuk...
                                </>
                            ) : "Sign In"}
                        </button>
                    </form>
                </div>

                {/* Copyright */}
                <p className="absolute bottom-6 text-center text-gray-400 text-xs">
                    &copy;{new Date().getFullYear()} Ranji Karya Sakti, All rights reserved.
                </p>
            </div>
        </div>
    );
};

export default LoginPage;
